import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Shield, Package, Truck, CheckCircle,
  Send, MessageSquare, CreditCard, MapPin, Calendar,
  Hash, ChevronDown, ChevronUp, Printer,
} from 'lucide-react';
import { useCustomer } from '../../context/CustomerContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  new: 'Order Received', payment_pending: 'Payment Pending', payment_verified: 'Payment Confirmed',
  validation_pending: 'Under Review', validation_hold: 'Needs Attention',
  ready_for_production: 'Queued for Production', batched: 'Scheduled for Build',
  in_production: 'In Production', serialized: 'Serial # Assigned',
  ready_to_ship: 'Ready to Ship', shipped: 'Shipped', cancelled: 'Cancelled',
};

const STATUS_COLOR = {
  new: 'text-zinc-400', payment_pending: 'text-yellow-400', payment_verified: 'text-blue-400',
  validation_pending: 'text-purple-400', validation_hold: 'text-red-400',
  ready_for_production: 'text-green-400', batched: 'text-cyan-400',
  in_production: 'text-blue-400', serialized: 'text-purple-400',
  ready_to_ship: 'text-orange-400', shipped: 'text-teal-400', cancelled: 'text-zinc-600',
};

const PIPELINE_ORDER = ['new','payment_pending','payment_verified','validation_pending',
  'validation_hold','ready_for_production','batched','in_production','serialized',
  'packed','ready_to_ship','shipped'];

const PROGRESS_STEPS = [
  { key: 'new',          label: 'Received',    desc: 'We got your order' },
  { key: 'payment_verified', label: 'Paid',    desc: 'Payment confirmed' },
  { key: 'in_production', label: 'Building',   desc: 'Your suppressor is being machined' },
  { key: 'serialized',   label: 'Serialized',  desc: 'Serial number assigned & verified' },
  { key: 'ready_to_ship', label: 'Packaged',   desc: 'Packed and ready for carrier' },
  { key: 'shipped',      label: 'Shipped',     desc: 'On its way to your FFL dealer' },
];

function isStepDone(stepKey, currentStatus) {
  const stepIdx = PIPELINE_ORDER.indexOf(stepKey);
  const curIdx = PIPELINE_ORDER.indexOf(currentStatus);
  if (stepIdx === -1 || curIdx === -1) return false;
  if (stepKey === 'payment_verified')
    return curIdx >= PIPELINE_ORDER.indexOf('payment_verified');
  if (stepKey === 'in_production')
    return curIdx >= PIPELINE_ORDER.indexOf('in_production');
  return curIdx >= stepIdx;
}

function SectionCard({ title, icon: Icon, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#1a1a1a] border border-white/10 overflow-hidden">
      <button
        className={`w-full flex items-center justify-between px-5 py-4 ${collapsible ? 'cursor-pointer hover:bg-white/3' : 'cursor-default'} border-b border-white/10`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <h3 className="font-bebas text-base text-white tracking-wider flex items-center gap-2">
          <Icon size={15} className="text-orange-500" />{title}
        </h3>
        {collapsible && (open ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />)}
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

export default function AccountOrderDetail() {
  const { id } = useParams();
  const { token, customer, loading: customerLoading } = useCustomer();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    if (!token) return;
    fetch(`${BASE}/api/customer/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setOrder(data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, token]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${BASE}/api/customer/orders/${id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim() }),
      });
      if (res.ok) { setMessage(''); load(); }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  if (customerLoading) return null;
  if (!customer) { navigate('/account/login'); return null; }

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!order) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-zinc-400 font-inter text-sm">
      Order not found. <Link to="/account" className="text-orange-400 ml-2">← My Orders</Link>
    </div>
  );

  const totalQty = order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
  const statusCfg = STATUS_COLOR[order.status] || 'text-zinc-400';
  const messages = order.messages || [];
  const adminMessages = messages.filter(m => m.from === 'admin');

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print styles — injected only on this page */}
      <style>{`
        @media print {
          /* Hide everything except the printable receipt */
          body { background: white !important; color: black !important; }
          .no-print { display: none !important; }
          .print-area {
            padding: 0 !important;
            max-width: 100% !important;
          }
          /* Flatten dark cards to white for print */
          .bg-\\[\\#1a1a1a\\], .bg-\\[\\#0f0f0f\\], .bg-\\[\\#141414\\], .bg-\\[\\#0a0a0a\\] {
            background: white !important;
          }
          /* Black text for readability */
          .text-white, .text-zinc-300, .text-zinc-400 { color: #111 !important; }
          .text-zinc-500, .text-zinc-600 { color: #555 !important; }
          .text-orange-400, .text-orange-500 { color: #c05a00 !important; }
          .text-teal-400 { color: #0d6e6e !important; }
          /* Borders */
          .border-white\\/10, .border-white\\/5 { border-color: #ddd !important; }
          /* Orange accents become dark for print */
          .bg-orange-500\\/10, .bg-orange-500\\/5 { background: #fff8f0 !important; }
          .border-orange-500\\/20 { border-color: #fbbf24 !important; }
          /* Add page break hints */
          .space-y-4 > * { page-break-inside: avoid; }
          /* Witt Machine branding at top of print */
          .print-header { display: block !important; }
        }
        @media screen {
          .print-header { display: none !important; }
        }
      `}</style>
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-4 print-area">

        {/* Print-only header (hidden on screen) */}
        <div className="print-header border-b-2 border-black pb-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div style={{fontFamily:'Arial',fontWeight:900,fontSize:'22px',letterSpacing:'0.1em',textTransform:'uppercase'}}>WITT MACHINE &amp; TOOL CO.</div>
              <div style={{fontFamily:'Arial',fontSize:'10px',color:'#666',letterSpacing:'0.2em',textTransform:'uppercase'}}>Texas, USA · SOT/Class III NFA Manufacturer</div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontFamily:'Arial',fontWeight:700,fontSize:'12px',letterSpacing:'0.15em',textTransform:'uppercase',color:'#c05a00',border:'1.5px solid #c05a00',padding:'4px 10px'}}>ORDER RECEIPT</div>
              <div style={{fontFamily:'Arial',fontSize:'9px',color:'#888',marginTop:'3px'}}>Printed {new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
            </div>
          </div>
        </div>

        {/* Back nav + Print button */}
        <div className="flex items-center justify-between mb-2 no-print">
          <div className="flex items-center gap-3">
            <Link to="/account" className="text-zinc-500 hover:text-white transition-colors">
              <ChevronLeft size={20} />
            </Link>
            <span className="font-inter text-sm text-zinc-500">My Orders</span>
            <ChevronLeft size={12} className="text-zinc-700 rotate-180" />
            <span className="font-inter text-sm text-zinc-300">{order.order_number}</span>
          </div>
          <button
            data-testid="print-order-btn"
            onClick={handlePrint}
            className="flex items-center gap-2 font-inter text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-orange-500/40 px-3 py-2 transition-colors"
            title="Print or Save as PDF"
          >
            <Printer size={13} />
            Print / Save PDF
          </button>
        </div>

        {/* ════════════════════════════════════════════════════════
            SECTION 1 — RECEIPT (purchase summary, shown first)
            ════════════════════════════════════════════════════ */}
        <div className="bg-[#1a1a1a] border border-white/10 overflow-hidden">
          {/* Receipt header */}
          <div className="border-b border-white/10 px-5 py-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bebas text-2xl text-white tracking-wider">{order.order_number}</div>
                <div className={`font-inter text-xs font-semibold mt-0.5 ${statusCfg}`}>
                  {STATUS_LABEL[order.status] || order.status}
                </div>
              </div>
              <div className="text-right flex flex-col items-end gap-2">
                <div className="font-bebas text-3xl text-orange-400 tracking-wide">${order.subtotal?.toFixed(2)}</div>
                <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">
                  {order.payment_method === 'stripe' ? 'Paid via Stripe'
                    : order.payment_method === 'demo_card' ? 'Test Payment'
                    : order.payment_method || 'Online Purchase'}
                </div>
                {/* Print button — always visible here */}
                <button
                  data-testid="print-order-btn"
                  onClick={handlePrint}
                  className="no-print flex items-center gap-1.5 font-inter text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-orange-500/40 px-3 py-1.5 transition-colors mt-1"
                  title="Print or Save as PDF"
                >
                  <Printer size={12} />
                  Print / Save PDF
                </button>
              </div>
            </div>
          </div>

          {/* Date + customer */}
          <div className="grid grid-cols-2 gap-0 border-b border-white/10">
            <div className="px-5 py-3 border-r border-white/10">
              <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mb-0.5">Date of Purchase</p>
              <div className="flex items-center gap-1.5">
                <Calendar size={12} className="text-zinc-500" />
                <p className="font-inter text-sm text-white">
                  {order.created_at
                    ? new Date(order.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : '—'}
                </p>
              </div>
              <p className="font-inter text-[10px] text-zinc-600 mt-0.5">
                {order.created_at ? new Date(order.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
              </p>
            </div>
            <div className="px-5 py-3">
              <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mb-0.5">Customer</p>
              <p className="font-inter text-sm text-white">{order.customer_info?.name || '—'}</p>
              <p className="font-inter text-[10px] text-zinc-600">{order.customer_info?.email || ''}</p>
            </div>
          </div>

          {/* Items table */}
          <div className="border-b border-white/10">
            <div className="px-5 py-2 border-b border-white/5">
              <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider">Items Ordered</p>
            </div>
            {order.items?.map((item, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3 border-b border-white/5 last:border-0">
                <div className="w-10 h-10 bg-[#0f0f0f] border border-white/5 flex-shrink-0 overflow-hidden">
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-full h-full object-contain p-1"
                    onError={e => { e.target.style.opacity = '0.2'; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-inter text-sm text-white font-medium leading-tight">{item.product_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.nfa_item && (
                      <span className="font-inter text-[9px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5">NFA ITEM</span>
                    )}
                    <span className="font-inter text-[10px] text-zinc-600">${item.unit_price?.toFixed(2)} each</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-inter text-sm text-white">×{item.quantity}</p>
                  <p className="font-inter text-xs text-zinc-400">${(item.unit_price * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}
            {/* Subtotal row */}
            <div className="flex items-center justify-between px-5 py-3 bg-[#141414]">
              <p className="font-inter text-xs text-zinc-500">{totalQty} item{totalQty !== 1 ? 's' : ''} · {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}</p>
              <p className="font-bebas text-xl text-white tracking-wide">${order.subtotal?.toFixed(2)}</p>
            </div>
          </div>

          {/* FFL Dealer & Addresses */}
          <div className={`grid gap-0 border-b border-white/10 ${order.ffl_dealer?.business_name ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {/* Billing address */}
            <div className={`px-5 py-4 ${order.ffl_dealer?.business_name ? 'border-r border-white/10' : ''}`}>
              <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mb-2">
                {order.has_nfa_items ? 'Billing Address' : 'Shipping Address'}
              </p>
              <div className="flex items-start gap-1.5">
                <MapPin size={11} className="text-zinc-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-inter text-xs text-white">{order.shipping_address?.address || '—'}</p>
                  <p className="font-inter text-xs text-zinc-400">
                    {[order.shipping_address?.city, order.shipping_address?.state, order.shipping_address?.zip].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            </div>
            {/* FFL Dealer */}
            {order.ffl_dealer?.business_name && (
              <div className="px-5 py-4">
                <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Shield size={9} className="text-orange-500" />FFL Transfer Dealer
                </p>
                <p className="font-inter text-sm text-white font-semibold">{order.ffl_dealer.business_name}</p>
                <p className="font-inter text-xs text-zinc-400">
                  {[order.ffl_dealer.address, order.ffl_dealer.city, order.ffl_dealer.state].filter(Boolean).join(', ')}
                </p>
                {order.ffl_dealer.license_number && (
                  <p className="font-inter text-[10px] text-zinc-600 mt-1">FFL: {order.ffl_dealer.license_number}</p>
                )}
              </div>
            )}
          </div>

          {/* NFA notice */}
          {order.has_nfa_items && (
            <div className="px-5 py-3 bg-orange-500/5 border-t border-orange-500/20">
              <p className="font-inter text-xs text-orange-300 leading-relaxed">
                <strong>NFA Item:</strong> Your suppressor will ship to your FFL dealer above.
                You'll complete the ATF Form 4 transfer there. An ATF approval period applies.
              </p>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════
            SECTION 2 — ORDER PROGRESS (pipeline tracker)
            ════════════════════════════════════════════════════ */}
        <SectionCard title="ORDER PROGRESS" icon={Package}>
          <div className="space-y-3">
            {PROGRESS_STEPS.map((step, i) => {
              const done = isStepDone(step.key, order.status);
              const isCurrentStep = PROGRESS_ORDER_CHECK(step.key, order.status);
              return (
                <div key={step.key} className="flex items-start gap-4">
                  {/* Dot + line */}
                  <div className="flex flex-col items-center gap-0 flex-shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${
                      done
                        ? 'bg-orange-500 border-orange-500'
                        : isCurrentStep
                        ? 'bg-transparent border-orange-500 animate-pulse'
                        : 'bg-transparent border-zinc-700'
                    }`}>
                      {done
                        ? <CheckCircle size={14} className="text-white" />
                        : <div className={`w-2 h-2 rounded-full ${isCurrentStep ? 'bg-orange-500' : 'bg-zinc-700'}`} />
                      }
                    </div>
                    {i < PROGRESS_STEPS.length - 1 && (
                      <div className={`w-px h-5 mt-1 ${done ? 'bg-orange-500/40' : 'bg-zinc-800'}`} />
                    )}
                  </div>

                  {/* Content */}
                  <div className={`pb-3 flex-1 ${i === PROGRESS_STEPS.length - 1 ? 'pb-0' : ''}`}>
                    <p className={`font-inter text-sm font-semibold ${done ? 'text-white' : isCurrentStep ? 'text-orange-400' : 'text-zinc-600'}`}>
                      {step.label}
                    </p>
                    <p className={`font-inter text-xs mt-0.5 ${done ? 'text-zinc-400' : 'text-zinc-700'}`}>
                      {step.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* ════════════════════════════════════════════════════════
            SECTION 3 — TRACKING DETAILS (serial + shipping)
            ════════════════════════════════════════════════════ */}
        {(order.serials?.length > 0 || order.shipment?.tracking_number) && (
          <SectionCard title="SERIAL NUMBER & TRACKING" icon={Hash} collapsible defaultOpen>
            {order.serials?.length > 0 && (
              <div className="mb-4">
                <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">
                  Assigned Serial Number{order.serials.length > 1 ? 's' : ''}
                </p>
                <div className="space-y-1.5">
                  {order.serials.map(s => (
                    <div key={s.serial_number} className="flex items-center justify-between bg-[#0f0f0f] border border-white/5 px-3 py-2">
                      <span className="font-mono text-sm font-bold text-orange-400">{s.serial_number}</span>
                      <span className="font-inter text-xs text-zinc-500">{s.product_name}</span>
                    </div>
                  ))}
                </div>
                <p className="font-inter text-[10px] text-zinc-600 mt-2">
                  Record this serial number for your ATF Form 4 transfer paperwork.
                </p>
              </div>
            )}
            {order.shipment?.tracking_number && (
              <div className="bg-teal-500/10 border border-teal-500/20 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Truck size={14} className="text-teal-400" />
                  <p className="font-bebas text-base text-teal-400 tracking-wider">SHIPMENT CONFIRMED</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider">Carrier</p>
                    <p className="font-inter text-sm text-white font-semibold">{order.shipment.carrier}</p>
                  </div>
                  <div>
                    <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider">Tracking Number</p>
                    <p className="font-mono text-sm text-teal-400 font-semibold">{order.shipment.tracking_number}</p>
                  </div>
                </div>
                <p className="font-inter text-xs text-zinc-500 mt-3">
                  Shipping to: <strong className="text-white">{order.ffl_dealer?.business_name}</strong>
                  {order.ffl_dealer?.city && ` — ${order.ffl_dealer.city}, ${order.ffl_dealer.state}`}
                </p>
              </div>
            )}
          </SectionCard>
        )}

        {/* ════════════════════════════════════════════════════════
            SECTION 4 — QUESTIONS / MESSAGES (hidden when printing)
            ════════════════════════════════════════════════════ */}
        <div className="no-print">
        <SectionCard
          title={`QUESTIONS & UPDATES${adminMessages.length > 0 ? ` (${adminMessages.length} from Witt Machine)` : ''}`}
          icon={MessageSquare}
          collapsible
          defaultOpen={messages.length > 0}
        >
          {messages.length > 0 ? (
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {messages.map(m => (
                <div key={m.message_id} className={`px-3 py-2.5 ${
                  m.from === 'customer'
                    ? 'bg-[#111] border border-white/5 ml-6'
                    : 'bg-orange-500/10 border border-orange-500/20'
                }`}>
                  <p className={`font-inter text-[10px] mb-1 ${m.from === 'customer' ? 'text-zinc-600' : 'text-orange-400'}`}>
                    {m.from === 'admin' ? '📩 Witt Machine Team' : 'You'} · {m.timestamp ? new Date(m.timestamp).toLocaleString() : ''}
                  </p>
                  <p className="font-inter text-sm text-white">{m.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="font-inter text-xs text-zinc-600 mb-4">
              Have a question about your order? Send us a message and we'll get back to you.
            </p>
          )}

          <div className="flex gap-2">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Ask a question about your order..."
              rows={2}
              className="flex-1 bg-[#111] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors resize-none placeholder-zinc-600"
            />
            <button
              onClick={handleSendMessage}
              disabled={sending || !message.trim()}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bebas text-sm tracking-widest px-4 transition-colors"
            >
              <Send size={14} />{sending ? '...' : 'SEND'}
            </button>
          </div>
        </SectionCard>
        </div>

        <div className="text-center pt-2 no-print">
          <Link to="/account" className="font-inter text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
            ← Back to My Orders
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}

// Helper: is this the "current active" step (not yet done, but next)
function PROGRESS_ORDER_CHECK(stepKey, currentStatus) {
  const curIdx = PIPELINE_ORDER.indexOf(currentStatus);
  const stepIdx = PIPELINE_ORDER.indexOf(stepKey);
  if (stepIdx === -1 || curIdx === -1) return false;
  // A step is "active/current" when it's the step the order is currently AT
  if (stepKey === 'new') return curIdx <= PIPELINE_ORDER.indexOf('payment_pending');
  if (stepKey === 'payment_verified') return ['payment_pending','payment_verified','validation_pending','validation_hold'].includes(currentStatus);
  if (stepKey === 'in_production') return ['ready_for_production','batched','in_production'].includes(currentStatus);
  if (stepKey === 'serialized') return currentStatus === 'serialized';
  if (stepKey === 'ready_to_ship') return currentStatus === 'ready_to_ship';
  if (stepKey === 'shipped') return currentStatus === 'shipped';
  return false;
}
